import Section from 'components/section';
import { renderRule, StructuredText } from 'react-datocms';
import {
  Document,
  isParagraph,
  isBlockquote,
  isList,
  isRoot,
  Span,
  isHeading,
} from 'datocms-structured-text-utils';
import clsx from 'clsx';
import { graphql } from 'gatsby';
import Video from 'components/video';
import React from 'react';
import Column, { ColumnResponsiveSizes } from 'components/column';
import Image from 'components/image';

import * as styles from './body.module.scss';
import Gallery from 'components/gallery';

export type BodyImageType = Pick<GatsbyTypes.DatoCmsBodyImage, 'id' | 'image' | 'caption'> & {
  __typename: 'DatoCmsBodyImage';
};
export type BodyVideoType = GatsbyTypes.DatoCmsBodyVideo & {
  __typename: 'DatoCmsBodyVideo';
};
export type BodyGalleryType = Pick<GatsbyTypes.GalleryFragment, 'slides'> &
  Omit<GatsbyTypes.DatoCmsBodyGallery, 'slides'> & {
    __typename: 'DatoCmsBodyGallery';
  };
type BlockType = BodyImageType | BodyVideoType | BodyGalleryType;

const Body: React.FC<{
  data: GatsbyTypes.ProjectBodyTextFragment | GatsbyTypes.NewsBodyTextFragment;
  className?: string;
}> = ({ data, className }) => {
  const sectionColumnConfig: ColumnResponsiveSizes = {
    xl: { width: 6, offset: 5 },
    lg: { width: 6, offset: 5 },
    md: { width: 6, offset: 5 },
    sm: { width: 8, offset: 3 },
    xs: { width: 10, offset: 2 },
  };

  if (
    data.value === null ||
    JSON.stringify((data.value as unknown as Document).document) ===
      '{"type":"root","children":[{"type":"paragraph","children":[{"type":"span","value":""}]}]}'
  )
    return null;

  return (
    <Section as={'article'} className={clsx(styles.body, className)} backgroundColor={'light'}>
      <Column xl={4} lg={4} md={4} sm={2} xs={2} className={styles.bodyInsetColumn} />
      <StructuredText
        data={data as unknown as Document}
        renderBlock={({ record }: { record: BlockType }) => {
          switch (record.__typename) {
            case 'DatoCmsBodyImage':
              return (
                <figure key={record.id} className={styles.bodyBlockImage}>
                  <Column
                    as={'figcaption'}
                    xl={3}
                    lg={3}
                    md={3}
                    sm={{ offset: 1, width: 7 }}
                    className={styles.bodyBlockImageCaption}
                  >
                    {record.caption}
                  </Column>
                  <Column xl={7} lg={7} md={8} sm={{ offset: 1, width: 11 }}>
                    <Image {...record.image} />
                  </Column>
                </figure>
              );
            case 'DatoCmsBodyVideo':
              return (
                <figure key={record.id} className={styles.bodyBlockImage}>
                  <Column
                    as={'figcaption'}
                    xl={3}
                    lg={3}
                    md={3}
                    sm={{ offset: 1, width: 7 }}
                    className={styles.bodyBlockImageCaption}
                  >
                    {record.caption}
                  </Column>
                  <Column xl={7} lg={7} md={8} sm={{ offset: 1, width: 11 }}>
                    {record.video && <Video url={record.video.url as string} />}
                  </Column>
                </figure>
              );
            case 'DatoCmsBodyGallery':
              return (
                <div key={record.id} className={styles.bodyBlockGallery}>
                  <Column
                    as={'figcaption'}
                    xl={3}
                    lg={3}
                    md={3}
                    sm={{ offset: 1, width: 7 }}
                    className={styles.bodyBlockGalleryCaption}
                  >
                    {record.caption}
                  </Column>
                  <Column xl={7} lg={7} md={8} sm={{ offset: 1, width: 11 }}>
                    <Gallery slides={record.slides} />
                  </Column>
                </div>
              );
            default:
              return null;
          }
        }}
        customRules={[
          renderRule(isList, ({ node, children, key }) => {
            if (node.style === 'bulleted') {
              return (
                <Column key={key} className={styles.bodyBlock} {...sectionColumnConfig}>
                  <ul>{children}</ul>
                </Column>
              );
            }
            if (node.style === 'numbered') {
              return (
                <Column key={key} className={styles.bodyBlock} {...sectionColumnConfig}>
                  <ol>{children}</ol>
                </Column>
              );
            }

            return null;
          }),
          renderRule(isParagraph, ({ children, ancestors, key }) => {
            if (isRoot(ancestors[0])) {
              return (
                <Column key={key} as={'p'} className={styles.bodyBlock} {...sectionColumnConfig}>
                  {children}
                </Column>
              );
            }

            return <p key={key}>{children}</p>;
          }),
          renderRule(isHeading, ({ node, children, ancestors, key }) => {
            if (isRoot(ancestors[0])) {
              return (
                <Column
                  key={key}
                  as={`h${node.level}`}
                  className={styles.bodyBlock}
                  {...sectionColumnConfig}
                >
                  {children}
                </Column>
              );
            }

            return null;
          }),
          renderRule(isBlockquote, ({ node, children, ancestors, key }) => {
            if (isRoot(ancestors[0])) {
              return (
                <Column
                  key={key}
                  as={'figure'}
                  className={clsx(styles.bodyBlock, styles.bodyBlockBlockquote)}
                  {...sectionColumnConfig}
                  xl={{ width: 5, offset: 5 }}
                >
                  <blockquote>{(node.children[0].children[0] as Span).value}</blockquote>
                  {node.children[1] && !node.attribution && (
                    <figcaption>{(node.children[1].children[0] as Span).value}</figcaption>
                  )}
                  {node.attribution && <figcaption>{node.attribution}</figcaption>}
                </Column>
              );
            }

            return null;
          }),
        ]}
      />
    </Section>
  );
};

export default React.memo(Body);

export const query = graphql`
  fragment BodyImage on DatoCmsBodyImage {
    __typename
    id: originalId
    caption
    image {
      ...Image
    }
  }
  fragment BodyVideo on DatoCmsBodyVideo {
    __typename
    id: originalId
    caption
    video {
      url
    }
  }
  fragment BodyGallery on DatoCmsBodyGallery {
    __typename
    id: originalId
    caption
    slides {
      ...GallerySlide
    }
  }
  fragment ProjectBodyText on DatoCmsDatoCmsProjectBodyTextStructuredText {
    value
    links
    blocks {
      ...BodyImage
      ...BodyVideo
      ...BodyGallery
    }
  }
  fragment NewsBodyText on DatoCmsDatoCmsNewsBodyTextStructuredText {
    value
    links
    blocks {
      ...BodyImage
      ...BodyVideo
      ...BodyGallery
    }
  }
`;
